import {cdnImage} from '@/util/cdnImage';
import {DEX_AGGREGATOR_META_DATA} from '@/util/metaData/crypto';
import PageHead from '@/components/PageHead';
import React from 'react';
import parse from 'html-react-parser';

// ─── getStaticProps ─────────────────────────────────────────────────────────
export const getStaticProps = async () => {
    const {
        dexAggregatorBannerBottom,
        dexAggregatorBannerPoint,
        dexAggregatorBusinessBenefit,
        DEXAggregatorCheckPoint,
        dexAggregatorChooseBody,
        dexAggregatorChooseContent,
        dexAggregatorDecodingData,
        dexAggregatorFAQData,
        dexAggregatorIndustry,
        dexAggregatorKeyBenefit,
        dexAggregatorMobileProcess,
        DEXAggregatorScreenButton,
        dexAggregatorServiceData,
        dexAggregatorStats,
        dexAggregatorTechnologyData
    } = await import('@/util/contents/dex-aggregator-data');

    return {
        props: {
            dexAggregatorBannerBottom,
            dexAggregatorBannerPoint,
            dexAggregatorBusinessBenefit,
            DEXAggregatorCheckPoint,
            dexAggregatorChooseBody,
            dexAggregatorChooseContent,
            dexAggregatorDecodingData,
            dexAggregatorFAQData,
            dexAggregatorIndustry,
            dexAggregatorKeyBenefit,
            dexAggregatorMobileProcess,
            DEXAggregatorScreenButton,
            dexAggregatorServiceData,
            dexAggregatorStats,
            dexAggregatorTechnologyData
        }
    };
};

// Sections are imported statically on purpose: next/dynamic gives every section its own render-blocking CSS file;
// static imports ship one page stylesheet. The JS is MUI-free and small.
import Container from '@/components/atoms/Container';
import Grid from '@/components/atoms/Grid';
import Icon from '@/components/atoms/Icon';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import Banner from '@/components/plain-sections/Banner';
import PlatformLogoStrip from '@/components/plain-sections/PlatformLogoStrip';
import Decoding from '@/components/plain-sections/Decoding';
import SpectrumHeader, {SpectrumSubHeading} from '@/components/plain-sections/SpectrumHeader';
import HelperSection, {HelperSectionGrid} from '@/components/plain-sections/HelperSection';
import Platforms from '@/components/plain-sections/Platforms';
import LatestCTA from '@/components/plain-sections/LatestCta';
import OurServices, {OurServicesGrid} from '@/components/plain-sections/OurServices';
import TokenizeHelp from '@/components/plain-sections/TokenizeHelp';
import DynamicScreens from '@/components/plain-sections/DynamicScreens';
import CardTechnology from '@/components/plain-sections/CardTechnology';
import MobileProcess from '@/components/plain-sections/MobileProcess';
import OurMakeChoice from '@/components/plain-sections/OurMakeChoice';
import LetsDiscuss from '@/components/plain-sections/LetsDiscuss';
import Questions from '@/components/plain-sections/Questions';
import AggregateScript from '@/components/Common/Aggregate-script';
import {CDN_URL, SITE_URL} from '@/constants/urls';
import styles from '@/styles/dexAggregator.module.scss';
import CvSection from '@/components/plain/CvSection';

const auditImg = cdnImage('dex-aggergator/DEX-Aggregators–Overview.webp');
const development = cdnImage('dex-aggergator/DEX-Aggregator-Development-Workflow.svg');
const statics = cdnImage('dex-aggergator/DEX-Aggregators-Market.webp');
const quantDax = cdnImage('dex-aggergator/QuantDax.webp');

// Logo strip heading: left-aligned up to 1024px, centred above (was useMediaQuery('(max-width:1024px)')).
const PARTNER_HEADING_ALIGN = {mobile: 'left', desktop: 'center'} as const;

const openContactPage = () => {
    globalThis.open('/contact-us', '_blank');
};

const DexAggregatorService = (props: any) => {
    const {
        openForm,
        dexAggregatorBannerBottom,
        dexAggregatorBannerPoint,
        dexAggregatorBusinessBenefit,
        DEXAggregatorCheckPoint,
        dexAggregatorChooseBody,
        dexAggregatorChooseContent,
        dexAggregatorDecodingData,
        dexAggregatorFAQData,
        dexAggregatorIndustry,
        dexAggregatorKeyBenefit,
        dexAggregatorMobileProcess,
        DEXAggregatorScreenButton,
        dexAggregatorServiceData,
        dexAggregatorStats,
        dexAggregatorTechnologyData
    } = props;
    return (
        <div>
            <PageHead meta={DEX_AGGREGATOR_META_DATA} url="/dex-aggregator-development" />
            <Banner
                priority={true}
                handleClickOpen={openForm}
                heading="DEX Aggregator Development Services"
                subheading="Redefine how your trading platforms access liquidity, execute trades, and deliver superior decentralized exchange experiences. Our DEX aggregator solutions unify fragmented liquidity pools, optimize trade execution, and provide end-to-end security-enabling faster transactions, minimized slippage, and maximized capital efficiency across multiple chains."
                button="Request a Free Consultation"
                image={cdnImage('dex-aggergator/DEX-Aggregator-Development-Services.webp')}
                alt="DEX Aggregator Development Services"
                DataPoints={dexAggregatorBannerPoint}
            />
            <PlatformLogoStrip
                heading="Our Strategic Partners"
                logos={dexAggregatorBannerBottom}
                logoLayout="stacked"
                headingAlign={PARTNER_HEADING_ALIGN}
            />
            <CvSection className="bottom-margin top-space decording-main-wrapper mobile-top-padding mobile-bottom-margin">
                <Container>
                    <Decoding
                        imageSrc={auditImg}
                        alt="DEX Aggregators - An Overview"
                        heading="DEX Aggregators - An Overview"
                        subHeading={dexAggregatorDecodingData}
                        isSocialMediaBtn={true}
                    />
                </Container>
            </CvSection>
            <hr className="hr-line zero-margin" />
            <CvSection className="bottom-spacing top-space ethereum-blockchain-enterprise mobile-top-padding mobile-bottom-padding">
                <Container>
                    <Grid container spacing={2}>
                        <Grid item md={6} xs={12}>
                            <SpectrumHeader
                                wrapperClassName="solutions trend-dominate-text-wrapper"
                                heading="DEX Aggregators Are Reshaping DeFi—Here’s Why You Should Build One Now"
                                headingClassName="color-black "
                                headingPb={2}
                                divider={false}
                            >
                                <SpectrumSubHeading>
                                    According to CoinGecko,{' '}
                                    <a target="_blank" href="/blog/dex-aggregators-vs-standalone-decentralized-exchanges">
                                        DEX aggregators
                                    </a>{' '}
                                    facilitated over $38 billion in monthly trading volume across chains in 2023 alone, and that number is
                                    climbing as more capital flows into decentralized ecosystems. Meanwhile, Messari reports that
                                    multi-chain trading volumes grew 79% YoY, with aggregators playing a pivotal role in enabling seamless
                                    cross-chain asset swaps.
                                </SpectrumSubHeading>
                                <SpectrumSubHeading>
                                    At Debut Infotech, we offer end-to-end DEX aggregator development tailored for startups, DeFi protocols,
                                    and exchanges looking to gain a competitive edge.
                                </SpectrumSubHeading>
                            </SpectrumHeader>
                            <div>
                                {dexAggregatorStats?.map((item: any, index: number) => (
                                    <div className="done-icon-wrapper" key={index + 1}>
                                        <Icon name="Done" className={styles.doneIcon} />
                                        <div>
                                            <p className={`done-subheading color-black audit-done-sub-heading ${styles.doneText}`}>
                                                {parse(item.subheading)}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                                <SpectrumHeader wrapperClassName="solutions trend-dominate-text-wrapper" wrapperPt={2} divider={false}>
                                    <SpectrumSubHeading>
                                        The aggregator market isn’t just maturing—it’s multiplying. With DeFi total value locked (TVL)
                                        recovering and cross-chain liquidity becoming the new standard,{' '}
                                        <span>DEX aggregators are poised to handle over $100B+ in monthly volume by 2026</span>, according
                                        to projections by DeFiLlama and The Block.
                                    </SpectrumSubHeading>
                                </SpectrumHeader>
                                <div className={styles.buttonRow}>
                                    <button
                                        type="button"
                                        className={`nft-button animated-button ${styles.strategyButton}`}
                                        onClick={openForm}
                                    >
                                        Book a Free Strategy Session
                                    </button>
                                </div>
                            </div>
                        </Grid>
                        <Grid item md={6} xs={12} style={{display: 'flex'}} justifyContent="center" alignItems="center">
                            <div className={`defi-enterprise-growth dex-aggregator-market-wrapper ${styles.marketImage}`}>
                                <OptimizedImage src={statics} alt="Blockchain for Innovation and Growth" />
                            </div>
                        </Grid>
                    </Grid>
                </Container>
            </CvSection>
            <CvSection className="helper-section">
                <div className="helper_section_parent bottom-spacing top-space mobile-top-padding mobile-bottom-padding">
                    <Container>
                        <SpectrumHeader
                            heading="DEX Aggregator Development Services We Offer"
                            headingClassName="color-black our-service-main-heading text-align-left"
                            divider="left"
                        />
                        <HelperSectionGrid>
                            <HelperSection data={dexAggregatorServiceData} className="home-helper-card" />
                        </HelperSectionGrid>
                    </Container>
                </div>
            </CvSection>
            <CvSection className="blockchain-revolution-service bottom-spacing top-space mobile-top-padding mobile-bottom-padding">
                <Container>
                    <Platforms
                        data={dexAggregatorKeyBenefit}
                        heading="Key Features of Our DEX Aggregator Platform"
                        containerClass="blockchain-card-container"
                        className="platform-key-industry-service no-border"
                    />
                </Container>
            </CvSection>
            <CvSection className="bottom-spacing  mobile-bottom-padding">
                <LatestCTA
                    heading="Get Your Platform Off the Ground"
                    subheading="Tap into our ready-to-brand DEX aggregator to pull in liquidity, cut slippage, and win cross-chain trades. We’ll walk you through every step to launch fast."
                    buttonText="Set Up a Call"
                    openForm={openForm}
                    className="blue-background-cta"
                />
            </CvSection>
            <CvSection className="helper-section">
                <div className="helper_section_parent bottom-spacing top-space mobile-top-padding">
                    <OurServicesGrid className="our-service-defi-wrapper">
                        <OurServices
                            ourServices={dexAggregatorBusinessBenefit}
                            className="service-shadow-border-box enterprise-key-card"
                            containerClass="card-heading-spacing"
                            heading="Business Benefits of Decentralized Exchange (DEX) Aggregators"
                            subheading="At Debut Infotech, a leading decentralized exchange development company, we engineer high-performance DEX aggregator platforms that unlock the true power of DeFi—helping you scale user acquisition, optimize trading logic, and maximize capital efficiency. Here's how our solutions deliver tangible value:"
                        />
                    </OurServicesGrid>
                </div>
            </CvSection>

            <CvSection className="bottom-spacing mobile-bottom-padding top-space mobile-top-padding">
                <Container>
                    <TokenizeHelp heading="Industries We Serve " contentData={dexAggregatorIndustry} />
                </Container>
            </CvSection>
            <CvSection className="bottom-spacing  mobile-bottom-padding">
                <LatestCTA
                    heading="Get Your Sector on the DeFi Fast Track"
                    subheading="Work with our experts to set up custom DEX aggregator solutions that drive growth and keep you ahead of the curve in any market you’re in."
                    buttonText="Schedule a Quick Chat"
                    openForm={openForm}
                    className="blue-background-cta"
                />
            </CvSection>
            <CvSection className="dynmaic-white-label-wrapper bottom-margin ">
                <Container>
                    <SpectrumHeader heading="Our Dex Aggregator Development Portfolio" headingClassName="color-black " />
                    <DynamicScreens
                        imageSrc={quantDax}
                        heading="An Enterprise-Ready DEX Aggregator Platform"
                        subPoints={[
                            'To redefine decentralized trading and liquidity access, a global DeFi venture partnered with Debut Infotech to build a next-generation DEX aggregator that delivers native cross-chain swaps, yield integration, portfolio tracking, and real-time token safety—all within a sleek, mobile-ready experience.'
                        ]}
                        reverseOrder={true}
                        alt="An Enterprise-Ready DEX Aggregator Platform"
                        checkItems={DEXAggregatorCheckPoint}
                        openForm={openForm}
                        buttonData={DEXAggregatorScreenButton}
                    />
                </Container>
            </CvSection>
            <CvSection className="bottom-spacing generative-tech-wrapper mobile-bottom-padding top-space mobile-top-padding helper_section_parent">
                <CardTechnology
                    heading="Our Technology Arsenal Powering DEX Aggregator Development"
                    subheading="Delivering robust DEX aggregator solutions that combine real-time trade execution, multi-chain liquidity access, and advanced smart contract orchestration, designed for efficiency, transparency, and high-performance trading outcomes at scale."
                    data={dexAggregatorTechnologyData}
                />
            </CvSection>
            <CvSection className="bottom-spacing process-mobile-bottom-padding top-space mobile-top-padding">
                <Container>
                    <SpectrumHeader
                        heading="Our End-to-End DEX Aggregator Development Workflow"
                        headingClassName="color-black text-align-left"
                        divider="left"
                        subheading="We leverage a structured, agile development process to build high-performance DEX aggregators. From design and integration to testing and deployment, each phase ensures scalability, security, and seamless cross-chain trade execution."
                        subheadingClassName="color-black spacing text-align-left"
                        subheadingBox
                    />
                    <div className={styles.centered}>
                        <OptimizedImage src={development} alt="Enterprise‑Grade Solutions" className="approach-main-img approach-web-img" />
                    </div>
                    <div className="approach-main-img approach-mobile-img">
                        <MobileProcess mobileProcess={dexAggregatorMobileProcess} />
                    </div>
                </Container>
            </CvSection>
            <OurMakeChoice
                heading="Why Debut Infotech is a Leading"
                subheading="DEX Aggregator Development Partner?"
                body={dexAggregatorChooseBody}
                contentData={dexAggregatorChooseContent}
            />
            <CvSection className="bottom-spacing">
                {/* The original's imageSx (right: 360px) never applied: the global .white-label-sprill rule always won. */}
                <LetsDiscuss
                    heading="Still on the Fence About Your DEX Project?"
                    subheading="No need to wait it out. We can clear up questions, map the roadmap, and help you get the ball rolling right away."
                    btnLabel="Book a Free Strategy Call"
                    className="white-label-sprill dex-aggregator-sprill"
                    openForm={openContactPage}
                />
            </CvSection>
            <CvSection className="question-parent top-space mobile-top-padding">
                <Container>
                    <Questions accordianData={dexAggregatorFAQData} />
                </Container>
            </CvSection>
            <script
                type="application/ld+json"
                id="dex-aggregator-development"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'FAQPage',
                        mainEntity: [
                            {
                                '@type': 'Question',
                                name: 'What is a DEX aggregator?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'A DEX aggregator is a DeFi platform that unifies liquidity from multiple decentralized exchanges like Uniswap, PancakeSwap, and Curve. It scans prices, pools, and routes across chains to execute token swaps at the best rates. Traders benefit from minimized slippage, faster execution, and lower gas costs while retaining full control of their assets through non-custodial smart contracts.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How does a DEX aggregator work?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'A DEX aggregator uses intelligent smart routing to analyze prices, liquidity depth, and network fees across numerous decentralized exchanges in real time. It splits or batches trades across multiple pools to secure optimal prices and efficient gas usage. Orders are executed through secure on-chain smart contracts, ensuring transparency and preserving user asset custody throughout the process.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'What is the difference between a DEX and a DEX aggregator?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'A DEX is a single decentralized marketplace where token swaps occur via smart contracts. A DEX aggregator, by contrast, connects to many DEXs and chains simultaneously. It compares rates, liquidity, and gas fees across those venues and automatically routes orders to achieve the best trade execution—offering deeper liquidity and better pricing than any standalone DEX.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'Why are DEX aggregators important in DeFi?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'DEX aggregators solve the DeFi liquidity fragmentation problem. By pooling and routing orders across multiple exchanges and blockchains, they provide traders with tighter spreads, reduced slippage, and efficient cross-chain swaps. For DeFi protocols, aggregators attract more users, increase volume, and improve capital efficiency, making them a critical infrastructure layer for next-generation decentralized trading platforms.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How do DEX aggregators generate revenue?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'DEX aggregators typically earn from small swap fees, partner integrations, and premium features like advanced analytics or referral programs. Some platforms issue native tokens for staking or governance, adding incentive-based revenue. By routing high trading volumes efficiently, aggregators capture fees without holding user funds, creating sustainable income streams for both platform operators and liquidity partners.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How long does it take to develop a DEX aggregator?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Development timelines vary by complexity and features. A white-label DEX aggregator can launch in 10–16 weeks, covering discovery, architecture, smart contract creation, multi-chain integrations, and rigorous auditing. Fully custom solutions with advanced routing logic or proprietary UI/UX typically require four to six months, including stress testing and compliance review before production deployment.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'What are the compliance considerations for a DEX aggregator?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Compliance involves integrating optional KYC/AML modules, enabling jurisdiction-based access control, and providing transparent audit trails. Platforms should implement MEV protection, smart contract audits, and data privacy safeguards to meet regulatory expectations. By offering a non-custodial architecture and secure on-chain execution, a DEX aggregator can remain decentralized while supporting enterprise-grade security and regional compliance requirements.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How much does it cost to build a DEX aggregator?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Costs depend on scope, technology stack, and required integrations. A basic white-label solution might start around $80K–$120K, while a fully custom aggregator with advanced features like cross-chain swaps, multi-level analytics, and MEV protection can range from $150K–$300K. Ongoing expenses include smart contract audits, infrastructure hosting, monitoring, and continuous feature upgrades to keep pace with DeFi growth.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How does MEV protection work in a DEX aggregator?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Miner Extractable Value (MEV) protection shields traders from front-running and sandwich attacks. The aggregator routes transactions through private relays or uses time-delay encryption to hide trade details until execution. Dynamic gas estimation and stealth submission ensure trades settle at intended prices, preserving user funds and enhancing platform trust for institutional and retail traders alike.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'Can a DEX aggregator support cross-chain swaps?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Yes. Modern DEX aggregators integrate secure bridges and relayers to enable cross-chain swaps without users manually switching networks. Smart contracts handle token wrapping and gas optimization behind the scenes, allowing seamless trades between ecosystems like Ethereum, BNB Chain, Solana, and Polygon—expanding liquidity access and improving capital efficiency across multiple blockchains.'
                                }
                            }
                        ]
                    })
                }}
            />
            <AggregateScript
                name="DEX Aggregator Development"
                image={`${CDN_URL}?url=%2Fimages%2Fdex-aggergator%2FDEX-Aggregator-Development-Services.webp&w=750&q=75%22`}
                description="Our DEX Aggregator Development service empowers businesses to seamlessly integrate multiple decentralized exchanges into a single platform, ensuring optimal liquidity, best token prices, and efficient trading. Unlock faster swaps, reduced slippage, and a superior DeFi trading experience."
                offers={{
                    '@type': 'AggregateOffer',
                    url: `${SITE_URL}/dex-aggregator-development`,
                    priceCurrency: 'USD',
                    lowPrice: '5000',
                    highPrice: '50000',
                    offerCount: '10'
                }}
                aggregateRating={{
                    '@type': 'AggregateRating',
                    ratingValue: '4.8',
                    bestRating: '5',
                    worstRating: '2',
                    ratingCount: '499'
                }}
            />
        </div>
    );
};

export default DexAggregatorService;
DexAggregatorService.Layout = 'Main';
